import { fetchPollQuestions, getPollAnswers } from 'store/polling'
import { times, reduce, merge } from 'lodash'
import { getPollStatusText, getPollType } from 'utils/polling'
import { formatToFourDigits } from './index'
import moment from 'moment-timezone'

const getRecordDetails = (record) => {
  if (record?.status === 'pending') {
    return 'Attendee marked attendance after the grace time'
  }
  if (record?.status === 'rejected' || record?.status === 'approved') {
    const history = record?.decisionHistory || []
    const latest = history[history.length - 1]
    if (latest?.reason) return latest.reason
  }
  if (record?.status === 'superseded') {
    return record?.supersedingTargetTitle
      ? `Superseded by attendance at ${record.supersedingTargetTitle}`
      : 'Superseded by attendance'
  }
  return ''
}

export const downloadAttendanceReportInCSV = (attendeesAttendance, confName) => {
  const headings = [
    'Date',
    'Full Name',
    'Email',
    'Event/Session',
    'Session Start',
    'Session End',
    'Scan Time',
    'Status',
    'Reason'
  ]

  const entries = []
  attendeesAttendance?.forEach((attendee) => {
    const fullName = `${attendee?.lastName ?? ''}, ${attendee?.firstName ?? ''}`

    ;(attendee.attendance || []).forEach(({ date, records }) => {
      ;(records || [])
        .filter((record) => record?.status !== 'superseded')
        .forEach((record) => {
          entries.push({
            date,
            dateValue: moment(date, 'MM/DD/YYYY').valueOf(),
            targetStartValue: record?.targetStart ? moment(record.targetStart).valueOf() : 0,
            fullName,
            email: attendee?.email,
            record
          })
        })
    })
  })

  entries.sort((a, b) => a.dateValue - b.dateValue || a.targetStartValue - b.targetStartValue)

  const values = entries.map(({ date, fullName, email, record }) => {
    const data = [
      date?.toString() || '',
      fullName,
      email?.toString() || '',
      record?.targetTitle?.toString() || '',
      record?.targetStart ? moment(record.targetStart).format('h:mm A') : '',
      record?.targetEnd ? moment(record.targetEnd).format('h:mm A') : '',
      record?.scanTimeLabel?.toString() || '',
      record?.status?.toString() || '',
      getRecordDetails(record)
    ]
    return data
  })

  const rows = [headings, ...values]
  downloadCSVFile(rows, `${confName}_attendance_report`)
}

export const downloadAttendeesInCSV = (attendees, conference) => {
  const headings = [
    'Last Name',
    'First Name',
    'Position',
    'Email',
    'Phone Number',
    'Institution',
    'Conference'
  ]
  const values = attendees?.map((item) => [
    item?.lastName?.toString(),
    item?.firstName?.toString(),
    item?.position?.toString(),
    item?.email?.toString(),
    item?.phoneNumber?.toString(),
    item?.company?.toString(),
    conference?.toString()
  ])
  const rows = [headings, ...values]
  downloadCSVFile(rows, 'conference_attendees')
}

export const downloadPollResultsInCSV = async (poll) => {
  const Headings = [
    'Poll Title',
    'Poll Status',
    'Poll Type',
    'Question Title',
    'Option A',
    'Option B',
    'Option C',
    'Option D',
    'Option E',
    'Option F',
    'Option G',
    'Option A Count',
    'Option B Count',
    'Option C Count',
    'Option D Count',
    'Option E Count',
    'Option F Count',
    'Option G Count',
    'Total Count'
  ]
  const pollQuestions = await fetchPollQuestions(poll?.id)
  const pollAnswers = await getPollAnswers(poll?.id)

  const csvData = pollQuestions.map((question) => {
    const questionAnswers = pollAnswers?.filter((doc) => doc.questionId === question?.id)
    const totalCount = questionAnswers?.length ?? 0

    const optionsWithCount = question?.options?.map((option, idx) => {
      const optionCount =
        questionAnswers?.filter((doc) => doc?.optId === option?.optId)?.length ?? 0
      return {
        [`option${idx + 1}`]: option.opt,
        [`voteCount${idx + 1}`]: optionCount
      }
    })

    const optionsArr = pushDataMultipleTimes(optionsWithCount, 7) // max 7 options
    const mergedObject = reduce(
      optionsArr,
      (result, obj) => {
        return merge(result, obj)
      },
      {}
    )

    return {
      question: question.title,
      ...mergedObject,
      totalCount
    }
  })

  const values = csvData?.map((item) => [
    poll?.title,
    getPollStatusText(poll?.status).status,
    getPollType(poll),
    item?.question.toString(),
    item?.option1.toString(),
    item?.option2.toString(),
    item?.option3.toString(),
    item?.option4.toString(),
    item?.option5.toString(),
    item?.option6.toString(),
    item?.option7.toString(),
    item.voteCount1,
    item.voteCount2,
    item.voteCount3,
    item.voteCount4,
    item.voteCount5,
    item.voteCount6,
    item.voteCount7,
    item.totalCount
  ])

  const rows = [Headings, ...values]
  const fileName = `${poll?.speaker ?? 'Anonymous'}_${poll?.associatedTitle}`
  const trimmedFileName = trimFilename(fileName)
  downloadCSVFile(rows, trimmedFileName)
}

const getEmptyOption = (idx) => {
  return {
    [`option${idx + 1}`]: 'N/A',
    [`voteCount${idx + 1}`]: 0
  }
}

const pushDataMultipleTimes = (dataArray, maxLimit) => {
  const arrLength = dataArray.length
  if (!arrLength) {
    return []
  } else if (arrLength < maxLimit) {
    const time = maxLimit - arrLength
    times(time, (idx) => {
      const option = getEmptyOption(idx + arrLength)
      dataArray.push(option)
    })
    return dataArray
  } else return dataArray
}

export const downloadBadgeSubjectsInCSV = (subjectType, subjects) => {
  const headings = [
    'First Name',
    'Last Name',
    'Credential',
    'Position',
    'Organization',
    'City',
    'State',
    'Country',
    'Email',
    'Role',
    'Phone'
  ]

  const values = subjects?.map((badge) => {
    return [
      badge?.firstName?.toString(),
      badge?.lastName?.toString(),
      badge?.credential?.toString(),
      badge?.position?.toString(),
      badge?.organization?.toString(),
      badge?.city?.toString(),
      badge?.state?.toString(),
      badge?.country?.toString(),
      badge?.email?.toString(),
      badge?.role?.toString(),
      badge?.phoneNumber?.toString()
    ]
  })
  const rows = [headings, ...values]
  downloadCSVFile(rows, `${subjectType}_badges_data`)
}

export const downloadBadgesInCSVV1 = (subjectType, subjects) => {
  const headings = [
    'Counter',
    'First Name',
    'Last Name',
    'Email',
    'Credential',
    'Position',
    'Organization',
    'City',
    'State',
    'Country',
    'Phone',
    'Type'
  ]

  const values = subjects?.map((badge) => {
    return [
      formatToFourDigits(badge?.counter)?.toString(),
      badge?.firstName?.toString(),
      badge?.lastName?.toString(),
      badge?.email?.toString(),
      badge?.credential?.toString(),
      badge?.position?.toString(),
      badge?.organization?.toString(),
      badge?.city?.toString(),
      badge?.state?.toString(),
      badge?.country?.toString(),
      badge?.phone?.toString(),
      badge?.type?.toString()
    ]
  })
  const rows = [headings, ...values]
  downloadCSVFile(rows, `${subjectType}_badges_data`)
}

export const downloadCSVFile = (rows, fileName) => {
  const csvContent = rows
    .map((row) =>
      row
        .map((field) =>
          typeof field === 'string'
            ? `"${field.replace(/"/g, '""')}"`
            : typeof field === 'number'
            ? field
            : ''
        )
        .join(',')
    )
    .join('\n')
  const link = document.createElement('a')
  link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURI(csvContent))
  link.setAttribute('download', `${fileName}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const trimFilename = (filename) => {
  const maxLength = 255

  if (filename.length > maxLength) {
    return filename.slice(0, maxLength)
  }

  return filename
}

export const downloadExhibitorSampleCSV = () => {
  const headings = ['Company', 'Booth No', 'Website URL', 'Introduction', 'Message']
  const sampleData = [
    [
      'Acme Inc',
      'B-101',
      'https://www.acmeinc.com',
      'Leading provider of innovative solutions',
      'Visit our booth for exclusive discounts'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'exhibitor_sample_template')
}

export const downloadOrgRepresentativeSampleCSV = () => {
  const headings = [
    'Organization',
    'First Name',
    'Last Name',
    'Email',
    'Is Primary Contact',
    'Credential',
    'Position',
    'Phone',
    'City',
    'State',
    'Country'
  ]
  const sampleData = [
    [
      'Healthcare Inc',
      'John',
      'Doe',
      'john.doe@example.com',
      'Yes',
      'MD, FACS',
      'Surgeon',
      '123-456-7890',
      'New York',
      'NY',
      'USA'
    ],
    [
      'MedTech Corp',
      'Alex',
      'Adams',
      'alex.adams@example.com',
      'No',
      'MD',
      'CEO',
      '987-654-3210',
      'San Francisco',
      'CA',
      'USA'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'organization_representative_sample_template')
}

export const downloadAttendeesSampleCSV = () => {
  const headings = [
    'First Name',
    'Last Name',
    'Email',
    'Role',
    'Credential',
    'Position',
    'Organization',
    'Phone',
    'City',
    'State',
    'Country'
  ]
  const sampleData = [
    [
      'John',
      'Doe',
      'john.doe@example.com',
      'Attendee',
      'MD, FACS',
      'Surgeon',
      'Healthcare Inc',
      '123-456-7890',
      'New York',
      'NY',
      'USA'
    ],
    [
      'Alex',
      'Adams',
      'alex.adams@example.com',
      'Speaker',
      'MD',
      'CEO',
      'MedTech Corp',
      '987-654-3210',
      'San Francisco',
      'CA',
      'USA'
    ],
    [
      'Michel',
      'Johnson',
      'michel.johnson@example.com',
      'Moderator',
      'MD FACS',
      'CEO',
      'MedTech Corp',
      '917-634-3210',
      'San Francisco',
      'CA',
      'USA'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'attendee_sample_template')
}

export const downloadSponsorSampleCSV = () => {
  const headings = [
    'Company',
    'Booth No',
    'Website URL',
    'Introduction',
    'Message',
    'Banner Click URL'
  ]
  const sampleData = [
    [
      'Acme Sponsors Ltd',
      'S-201',
      'https://www.acmesponsors.com',
      'Premium sponsor of industry events',
      'Proud to support this conference',
      'https://www.acmesponsors.com/promotion'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'sponsor_sample_template')
}

export const downloadSpeakerSampleCSV = () => {
  const headings = [
    'First Name',
    'Last Name',
    'Email',
    'Role',
    'Credential',
    'Position',
    'Organization',
    'Phone',
    'City',
    'State',
    'Country',
    'Website URL',
    'Bio'
  ]
  const sampleData = [
    [
      'Jane',
      'Smith',
      'jane.smith@example.com',
      'Presenter',
      'MD, FACS',
      'Chief Technology Officer',
      'Tech Innovations Inc',
      '987-654-3210',
      'San Francisco',
      'CA',
      'USA',
      'https://www.techinnovations.com',
      'Jane Smith is a renowned expert in artificial intelligence with over 15 years of experience.'
    ],
    [
      'Michael',
      'Johnson',
      'michael.johnson@example.com',
      'Moderator',
      'PhD',
      'Director of Research',
      'Digital Solutions LLC',
      '555-987-6543',
      'Austin',
      'TX',
      'USA',
      'https://www.digitalsolutions.com',
      'Dr. Michael Johnson leads groundbreaking research in quantum computing.'
    ],
    [
      'Sarah',
      'Davis',
      'sarah.davis@example.com',
      'Presenter; Moderator',
      'PhD, MBA',
      'Director of Innovation',
      'Future Tech Corp',
      '555-123-4567',
      'Seattle',
      'WA',
      'USA',
      'https://www.futuretech.com',
      'Sarah Davis is an expert in both presenting and moderating technical sessions.'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'speaker_sample_template')
}

export const downloadOrganizationSampleCSV = () => {
  const headings = [
    'Organization',
    'Level',
    'Booth',
    'Website URL',
    'Introduction',
    'Message',
    'Banner Click URL'
  ]
  const sampleData = [
    [
      'Acme',
      'Exhibitor',
      '2',
      'https://www.acme.com',
      'Leading provider of innovative solutions',
      'Visit our booth for exclusive discounts',
      'https://www.acme.com/promotion'
    ],
    [
      'Walmart',
      'Silver',
      '3',
      'https://www.walmart.com',
      'Retail giant with global presence',
      'Great deals available at our booth',
      'https://www.walmart.com/conference'
    ],
    [
      'Mastercard',
      'Gold',
      '4',
      'https://www.mastercard.com',
      'Global payment solutions provider',
      'Learn about our latest payment technologies',
      'https://www.mastercard.com/solutions'
    ],
    []
  ]

  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'organization_sample_template')
}

export const downloadBuildingSampleCSV = () => {
  const rows = [
    ['Title', 'Floor', 'Room'],
    ['Main Hall', '1', '101'],
    ['Conference Room A', '2', '202']
  ]
  downloadCSVFile(rows, 'building_import_template')
}

export const downloadBadgeSubjectSampleCSV = () => {
  const headings = [
    'Counter',
    'First Name',
    'Last Name',
    'Email',
    'Credential',
    'Position',
    'Organization',
    'Phone',
    'City',
    'State',
    'Country'
  ]
  const sampleData = [
    [
      '1',
      'John',
      'Doe',
      'john.doe@example.com',
      'MD',
      'Surgeon',
      'Healthcare Inc',
      '123-456-7890',
      'New York',
      'NY',
      'USA'
    ],
    [
      '2',
      'Jane',
      'Smith',
      'jane.smith@example.com',
      'PhD',
      'Director',
      'Tech Corp',
      '987-654-3210',
      'Chicago',
      'IL',
      'USA'
    ]
  ]
  const rows = [headings, ...sampleData]
  downloadCSVFile(rows, 'badges_import_template')
}
